import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { Router } from '@angular/router'
import { ModalController } from '@ionic/angular'
import { Subject, Subscription, of } from 'rxjs'
import { catchError, debounceTime, switchMap, tap } from 'rxjs/operators'
import { GlobalController } from 'src/app/controllers/global.controller'
import { UnifiedSearchProvider } from 'src/app/providers/unifiedSearch.provider'
import { UnifiedSearchResult } from 'src/app/models/unifiedSearch.model'

enum SearchTypes {
  EMPLOYEE = 'employee',
  SUPERVISOR = 'supervisor',
  CLIENT = 'client',
  SITE = 'site',
  COMMUNICATION = 'communication',
}

// Minimum number of characters before firing a search request.
const MIN_SEARCH_LENGTH = 3
// Debounce between keystrokes and the actual API call (ms).
const SEARCH_DEBOUNCE_MS = 300

@Component({
  selector: 'app-unified-search',
  templateUrl: './unified-search.component.html',
  styleUrls: ['./unified-search.component.scss'],
})
export class UnifiedSearchComponent implements OnInit, OnDestroy {
  results: UnifiedSearchResult[] = []
  searchTerm: string = ''
  showInactive = false
  loading = false
  // True when the user has typed something but not enough characters to
  // trigger a search — lets the template show a hint.
  tooShort = false
  @ViewChild('searchInput') searchInput: any

  private searchTrigger$ = new Subject<void>()
  private searchSub?: Subscription

  /**
   * Records that contains the display information of the different data types returned by the search
   */
  // note = cle Transloco, traduite dans le template pour suivre un changement de langue.
  private displayInfoByType: Record<SearchTypes, { note: string; icon: string }> = {
    [SearchTypes.EMPLOYEE]: { note: 'UNIFIED_SEARCH.TYPE_EMPLOYEE', icon: 'person' },
    [SearchTypes.CLIENT]: { note: 'UNIFIED_SEARCH.TYPE_CLIENT', icon: 'happy' },
    [SearchTypes.SITE]: { note: 'UNIFIED_SEARCH.TYPE_SITE', icon: 'business' },
    [SearchTypes.COMMUNICATION]: { note: 'UNIFIED_SEARCH.TYPE_COMMUNICATION', icon: 'mail' },
    [SearchTypes.SUPERVISOR]: { note: 'UNIFIED_SEARCH.TYPE_SUPERVISOR', icon: 'megaphone' },
  }

  constructor(
    private globalCtrl: GlobalController,
    private searchProvider: UnifiedSearchProvider,
    private router: Router,
    private modalCtrl: ModalController
  ) {}

  ngOnInit() {
    this.searchSub = this.searchTrigger$
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        tap(() => {
          const term = this.searchTerm.trim()
          this.tooShort = term.length > 0 && term.length < MIN_SEARCH_LENGTH
          this.loading = term.length >= MIN_SEARCH_LENGTH
        }),
        switchMap(() => {
          const term = this.searchTerm.trim()
          if (term.length < MIN_SEARCH_LENGTH) {
            return of<UnifiedSearchResult[]>([])
          }
          return this.searchProvider.makeGlobalSearch(term, this.showInactive).pipe(
            catchError(err => {
              this.globalCtrl.showErrorMessage(err, 'Erreur lors de la recherche globale.')
              return of<UnifiedSearchResult[]>([])
            })
          )
        })
      )
      .subscribe(res => {
        this.results = res
        this.loading = false
      })
  }

  ngOnDestroy() {
    this.searchSub?.unsubscribe()
    this.searchTrigger$.complete()
  }

  ionViewDidEnter() {
    setTimeout(() => {
      this.searchInput.setFocus()
    }, 300)
  }

  /**
   * Called on every keystroke and on Enter. Actual search is debounced and
   * only fires once the search term reaches MIN_SEARCH_LENGTH characters.
   */
  onSearchInput() {
    const term = this.searchTerm.trim()
    if (term === '') {
      // Clear immediately without waiting for debounce when the input is empty.
      this.results = []
      this.tooShort = false
      this.loading = false
      return
    }
    this.searchTrigger$.next()
  }

  clearSelection() {
    this.searchTerm = ''
    this.results = []
    this.tooShort = false
    this.loading = false
  }

  getSearchTypesEnum() {
    return SearchTypes
  }

  getNoteByType(type: string) {
    return this.displayInfoByType[type as SearchTypes].note
  }

  getIconByType(type: string) {
    return this.displayInfoByType[type as SearchTypes].icon
  }

  seeDetails(selectedItem: UnifiedSearchResult) {
    this.closeModal()
    switch (selectedItem.type) {
      case SearchTypes.EMPLOYEE:
        this.router.navigateByUrl('employees/' + selectedItem._id)
        break
      case SearchTypes.CLIENT:
        this.router.navigateByUrl('clients/' + selectedItem._id)
        break
      case SearchTypes.SITE:
        this.router.navigateByUrl('clients/' + selectedItem.clientId, { state: { data: selectedItem._id } })
        break
      case SearchTypes.SUPERVISOR:
        this.router.navigateByUrl('supervisor/' + selectedItem._id)
        break
      case SearchTypes.COMMUNICATION:
        this.router.navigateByUrl('messages/' + selectedItem.employeeId, {
          state: { type: selectedItem.communicationType, messageId: selectedItem._id },
        })
        break
    }
  }

  changeInactiveToggle($event) {
    this.showInactive = $event.detail.checked
    this.onSearchInput()
  }

  closeModal() {
    this.modalCtrl.dismiss()
  }

  trackByFn(index: number, item: UnifiedSearchResult) {
    return item._id
  }

  // Exposed for the template.
  get minSearchLength(): number {
    return MIN_SEARCH_LENGTH
  }
}
